import { useCallback, useEffect, useState } from 'react'
import { Button, Tooltip, notification } from 'antd'
import styled from 'styled-components'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import moment from 'moment'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import {
	faTrash,
	faArrowUpRightFromSquare,
	faArrowsRotate,
	faCloudCheck,
	faFolderImage,
	faEye,
} from '@fortawesome/pro-duotone-svg-icons'

import { CmsPreviewContainer } from '../../../shared/oplevalt/cms/preview-container'
import { ImageCatalogue } from '../../../shared/oplevalt/cms/image-catalogue'
import { CmsSidebar } from '../../../shared/oplevalt/cms/sidebar/sidebar'
import { LayoutDrawer } from '../../../shared/oplevalt/cms/layouts'
import { DeleteModal } from '../../../shared/delete-modal'
import type { Layouts } from '../../../shared/oplevalt/cms/layouts/utils'
import { Spinner } from '../../../shared/spinner'

import * as CmsImageService from '../../../../services/bb-api/oplevalt/cms/image'
import * as CmsPageService from '../../../../services/bb-api/oplevalt/cms/page'
import * as CmsSectionService from '../../../../services/bb-api/oplevalt/cms/section'

import { txt } from '../../../../locales/i18n'
import { theme } from '../context'

export const CmsPage = () => {
	const [isLoading, setIsLoading] = useState(true)
	const [isLoadingSections, setIsLoadingSections] = useState(true)
	const [page, setPage] = useState<any>(null)
	const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null)
	const [showLayoutDrawer, setShowLayoutDrawer] = useState(false)
	const [deleteModelVisible, setDeleteModelVisible] = useState(false)
	const [sections, setSections] = useState<any[]>([])
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false)
	const [sectionsChanged, setSectionsChanged] = useState(new Set<number>())

	const { pageId } = useParams<{ pageId: string }>()
	const history = useHistory()

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (sectionsChanged.size !== 0) {
				event.preventDefault()
				event.returnValue = txt('global.unsaved changes')
			}
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [sectionsChanged])

	const getSections = useCallback(async () => {
		setIsLoadingSections(true)

		const sections = await CmsSectionService.query(Number(pageId))
		setSections(sections ?? [])

		setIsLoadingSections(false)
	}, [pageId])

	const getPage = useCallback(async () => {
		setIsLoading(true)

		try {
			const page = await CmsPageService.get(pageId)
			setPage(page)
		} catch (error) {
			history.goBack()
			notification.error({ message: 'Page not found' })
		}

		setIsLoading(false)
	}, [pageId, history])

	const handleUpdateAllSections = useCallback(async () => {
		const sectionsUpdateTasks = sections.map(async (section) => {
			try {
				const sectionUpdate = await CmsSectionService.update(pageId, section.id, section.content)

				return sectionUpdate
			} catch (error) {
				return section
			}
		})

		const sectionsUpdate = await Promise.all(sectionsUpdateTasks)
		const updateSuccessful = sectionsUpdate.every((section) => !section.unsaved)

		if (updateSuccessful) {
			setSectionsChanged(new Set())
			notification.success({ message: txt('notification.update successful') })
		}

		if (updateSuccessful === false) {
			notification.error({ message: txt('notification.update failed') })
		}

		setSections(sectionsUpdate)
		const page = await CmsPageService.get(pageId)
		setPage(page)
	}, [sections, pageId])

	useEffect(() => {
		getPage()
		getSections()
	}, [getPage, getSections])

	const handleSectionChange = (changedValues: any, values: any) => {
		setSectionsChanged((sectionsChanged) => {
			if (selectedSectionId) sectionsChanged.add(selectedSectionId)
			return new Set(sectionsChanged)
		})

		setSections((sections) => {
			return sections.map((section) => {
				if (section.id !== selectedSectionId) return section
				section.content = { ...section.content, ...values }
				section.unsaved = true
				return section
			})
		})
	}

	const handleSectionUpdate = async (sectionId: string, sectionUpdated: any) => {
		setSections((sections) => {
			return sections.map((section) => {
				if (section.id !== sectionId) return section

				setSectionsChanged((sectionsChanged) => {
					sectionsChanged.delete(Number(sectionId))
					return new Set(sectionsChanged)
				})

				if (section.layoutName === 'Counter') {
					const date = sectionUpdated.content.counterContainer.date
					sectionUpdated.content.counterContainer.date = moment(date)
				}

				return sectionUpdated
			})
		})

		const page = await CmsPageService.get(pageId)
		setPage(page)
	}

	const handleSectionDeletion = async (sectionId: string) => {
		await CmsSectionService.remove(pageId, sectionId)
		setSelectedSectionId(null)
		setSections((sections) => sections.filter((section: any) => section.id !== sectionId))
	}

	const handlePageDeletion = async () => {
		await CmsPageService.remove(pageId)
		history.replace('/oplevalt/cms')
	}

	const handleCreateSection = useCallback(
		async (layoutName: string, layout: Layouts[string]) => {
			const section = {
				layoutName,
				content: JSON.stringify(layout.values),
				index: sections.length,
			}

			const createdSection = await CmsSectionService.create(pageId, section)

			if (layoutName === 'Counter') {
				const date = moment(createdSection.content.counterContainer.date)
				createdSection.content.counterContainer.date = date
			}

			setSections((sections) => [...sections, createdSection])
			setSelectedSectionId(createdSection.id)
			setShowLayoutDrawer(false)
		},
		[pageId, sections]
	)

	const getSection = (selectedSectionId: number | null) => {
		const section = sections.find((section: any) => section.id === selectedSectionId)
		return section ?? null
	}

	const getBoblbergLink = (type: 'live' | 'preview') => {
		let link = process.env.REACT_APP_OPLEVALT_HOST + '/' + page.url
		if (type === 'preview') link += '?preview'
		return link
	}

	if (isLoading) return <Spinner />

	return (
		<Main>
			<Prompt when={sectionsChanged.size !== 0} message={txt('global.unsaved changes')} />

			<section className="topbar-container">
				<Tooltip title={txt('cms.add layout')}>
					<Button type="primary" onClick={() => setShowLayoutDrawer(true)}>
						<FontAwesomeIcon icon={faPlus} />
					</Button>
				</Tooltip>

				<div className="title-container">
					<span>{page?.title}</span>
				</div>

				<Tooltip title={txt('global.images')}>
					<Button type="link" onClick={() => setOpenImageCatalogue(true)}>
						<FontAwesomeIcon icon={faFolderImage} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('cms.save all')}>
					<Button type="link" onClick={handleUpdateAllSections}>
						<FontAwesomeIcon icon={faCloudCheck} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('cms.refresh previews')}>
					<Button type="link" onClick={getSections}>
						<FontAwesomeIcon icon={faArrowsRotate} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('cms.preview')}>
					<Button type="link" href={getBoblbergLink('preview')} target="_blank">
						<FontAwesomeIcon icon={faEye} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('cms.open page')}>
					<Button type="link" href={getBoblbergLink('live')} target="_blank">
						<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('cms.delete page')}>
					<Button type="link" danger onClick={() => setDeleteModelVisible(true)}>
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</Tooltip>
			</section>

			<section className="content-container" onClick={() => setSelectedSectionId(null)}>
				<CmsPreviewContainer
					isLoadingSections={isLoadingSections}
					pageId={pageId}
					sections={sections}
					previewType="desktop"
					onSectionSelection={setSelectedSectionId}
					selectedSectionId={selectedSectionId}
					openLayoutDrawer={() => setShowLayoutDrawer(true)}
				/>
			</section>

			<CmsSidebar
				page={page}
				setPage={setPage}
				className="sidebar-container"
				target={getSection(selectedSectionId)}
				setSelectedSectionId={setSelectedSectionId}
				onSectionChange={handleSectionChange}
				onSectionDeletion={handleSectionDeletion}
				onSectionUpdate={handleSectionUpdate}
			/>

			<LayoutDrawer
				visible={showLayoutDrawer}
				onClose={() => setShowLayoutDrawer(false)}
				onSelect={handleCreateSection}
			/>

			<DeleteModal onConfirm={handlePageDeletion} useVisible={[deleteModelVisible, setDeleteModelVisible]} />

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix="https://filer.oplevalt.dk/cms/large_"
				onQuery={CmsImageService.query}
				onCreate={CmsImageService.create}
				onRemove={CmsImageService.remove}
				showTransparent
			/>
		</Main>
	)
}

const Main = styled.main`
	background-color: ${theme.orange.neutral};
	display: grid;

	grid-template-columns: 1fr auto;
	grid-template-rows: auto 1fr;
	position: relative;

	.topbar-container {
		padding: 0.6rem 1rem;
		background-color: white;
		border-bottom: 1px solid #eee;
		z-index: 1;

		display: flex;
		align-items: center;
		gap: 1ch;

		grid-column: 1 / 3;

		.title-container {
			display: flex;
			flex-direction: column;
			margin-right: auto;

			a {
				font-size: 0.65rem;
			}
		}
	}

	.content-container {
		box-shadow: 0 0 1rem #00000009 inset;
		overflow-y: scroll;
		display: flex;
		justify-content: center;
		padding: 2ch;
	}

	.ant-drawer-body button {
		display: contents !important;

		.layout-container {
			aspect-ratio: 2/1;
		}
	}
`
